import { useState, useMemo, useEffect, useCallback } from "react";
function useColoringQuiz({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  onAnswerChange
}) {
  var _a, _b, _c, _d;
  const [startTime] = useState(() => Date.now());
  const [isShowingCorrectAnswers, setIsShowingCorrectAnswers] = useState(false);
  const isSolved = solveResult !== null && solveResult !== void 0;
  const digits = quiz.digits || 0;
  const rows = quiz.rows || 1;
  const inverted = quiz.inverted ?? false;
  const shouldShowCorrectAnswers = showReallyTrue || isShowingCorrectAnswers;
  const onImageSrc = ((_a = quiz.coloringOnImage) == null ? void 0 : _a.signedUrl) ?? ((_b = quiz.coloringOnImage) == null ? void 0 : _b.url) ?? null;
  const offImageSrc = ((_c = quiz.coloringOffImage) == null ? void 0 : _c.signedUrl) ?? ((_d = quiz.coloringOffImage) == null ? void 0 : _d.url) ?? null;
  const hasImages = !!(onImageSrc && offImageSrc);
  const computedInitialAnswers = useMemo(() => {
    if (initialAnswersProp && initialAnswersProp.length > 0 && initialAnswersProp[0].length > 0) {
      return [...initialAnswersProp[0]].map(Number);
    }
    if (isViewSolved && (solveResult == null ? void 0 : solveResult.correctAnswers) && solveResult.correctAnswers.length > 0) {
      return [...solveResult.correctAnswers[0]].map(Number);
    }
    return Array(digits).fill(inverted ? 1 : 0);
  }, [initialAnswersProp, isViewSolved, solveResult == null ? void 0 : solveResult.correctAnswers, digits, inverted]);
  const [answers, setAnswers] = useState(computedInitialAnswers);
  useEffect(() => {
    setAnswers(computedInitialAnswers);
  }, [computedInitialAnswers]);
  useEffect(() => {
    onAnswerChange == null ? void 0 : onAnswerChange([answers.join("")]);
  }, [answers, onAnswerChange]);
  const correctColorCount = useMemo(() => {
    if (!(solveResult == null ? void 0 : solveResult.correctAnswers) || solveResult.correctAnswers.length === 0) return null;
    const val = parseInt(solveResult.correctAnswers[0], 10);
    return isNaN(val) ? null : val;
  }, [solveResult == null ? void 0 : solveResult.correctAnswers]);
  const correctAnswerArray = useMemo(() => {
    if (correctColorCount === null) return null;
    const arr = Array(digits).fill(0);
    for (let i = 0; i < Math.min(correctColorCount, digits); i++) {
      arr[i] = 1;
    }
    return arr;
  }, [correctColorCount, digits]);
  const itemsPerRow = useMemo(() => Math.ceil(digits / rows), [digits, rows]);
  const rowIndices = useMemo(() => {
    const result = [];
    for (let r = 0; r < rows; r++) {
      const start = r * itemsPerRow;
      const end = Math.min(start + itemsPerRow, digits);
      const indices = [];
      for (let i = start; i < end; i++) {
        indices.push(i);
      }
      result.push(indices);
    }
    return result;
  }, [rows, itemsPerRow, digits]);
  const handleToggle = useCallback((index) => {
    if (isSolved || isViewSolved) return;
    setAnswers((prev) => {
      const next = [...prev];
      next[index] = next[index] === 1 ? 0 : 1;
      return next;
    });
  }, [isSolved, isViewSolved]);
  const handleSubmit = useCallback(() => {
    if (isSolved) return;
    const elapsedTime = Date.now() - startTime;
    onSolve == null ? void 0 : onSolve(quizId, [answers.join("")], quiz.vars, elapsedTime);
  }, [isSolved, startTime, onSolve, quizId, answers, quiz.vars]);
  const coloredCount = answers.filter((a) => a === 1).length;
  const getImageSrc = useCallback((index) => {
    if (isSolved && shouldShowCorrectAnswers && correctAnswerArray) {
      return correctAnswerArray[index] === 1 ? onImageSrc ?? "" : offImageSrc ?? "";
    }
    return answers[index] === 1 ? onImageSrc ?? "" : offImageSrc ?? "";
  }, [isSolved, shouldShowCorrectAnswers, correctAnswerArray, answers, onImageSrc, offImageSrc]);
  const getIsColored = useCallback((index) => {
    if (isSolved && shouldShowCorrectAnswers && correctAnswerArray) {
      return correctAnswerArray[index] === 1;
    }
    return answers[index] === 1;
  }, [isSolved, shouldShowCorrectAnswers, correctAnswerArray, answers]);
  return {
    answers,
    isSolved,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    digits,
    rows,
    inverted,
    hasImages,
    onImageSrc,
    offImageSrc,
    correctColorCount,
    correctAnswerArray,
    itemsPerRow,
    rowIndices,
    coloredCount,
    handleToggle,
    handleSubmit,
    getImageSrc,
    getIsColored,
    quiz,
    quizId,
    solveResult,
    isViewSolved
  };
}
export {
  useColoringQuiz
};
//# sourceMappingURL=use-coloring-quiz.js.map
